import { encodePart } from "./escape";
import { AUTHORITY_PARTS, type Pair, type PartKey, type UrlParts } from "./parts";

export function writeUrl(parts: UrlParts): string {
  const authority = parts.slashes ? `//${writeUserinfo(parts)}${writeHost(parts)}` : "";
  // Under an authority a path is rooted, or the text after the host reads straight back as more of the host.
  const path = authority && parts.path && !parts.path.startsWith("/") ? `/${parts.path}` : parts.path;
  const scheme = parts.scheme ? `${parts.scheme}:` : "";
  const query = parts.query === null ? "" : `?${parts.query}`;
  const fragment = parts.fragment === null ? "" : `#${parts.fragment}`;
  return `${scheme}${authority}${path}${query}${fragment}`;
}

export function withPart(parts: UrlParts, key: PartKey, value: string): UrlParts {
  // Emptying either of these two takes its delimiter with it: a `?` with no query left behind it is not a query.
  if (key === "query" || key === "fragment") return { ...parts, [key]: value === "" ? null : value };
  return { ...parts, [key]: value, slashes: parts.slashes || (AUTHORITY_PARTS.has(key) && value !== "") };
}

export function withPairs(parts: UrlParts, pairs: Pair[]): UrlParts {
  return { ...parts, query: pairs.length === 0 ? null : pairs.map((pair) => pair.raw).join("&") };
}

// Only the parameter being typed in is written afresh; every other keeps the text it was read from, so editing one
// leaves the spelling of the rest exactly as the address had it — a `+` for a space, an unescaped comma, whatever.
export function editPair(pair: Pair, patch: { name?: string; value?: string }): Pair {
  const name = patch.name ?? pair.name;
  const value = patch.value ?? pair.value;
  // Typing into a flag's value gives it one, and after that the address says so; emptied of its name there is no flag
  // left to keep either, and the `=` is what holds the row's place in the query rather than closing it up.
  const bare = pair.bare && value === "" && name !== "";
  const raw = bare ? encodePart(name) : `${encodePart(name)}=${encodePart(value)}`;
  return { name, value, bare, raw, nameError: null, valueError: null };
}

// A parameter with nothing in it yet still holds its place in the query, so the row survives the round trip back
// through the address; the `=` is also what makes the next keystroke a name rather than a flag.
export function newPair(): Pair {
  return { name: "", value: "", bare: false, raw: "=", nameError: null, valueError: null };
}

function writeUserinfo({ username, password }: UrlParts): string {
  if (!username && !password) return "";
  return `${username}${password ? `:${password}` : ""}@`;
}

function writeHost({ host, port }: UrlParts): string {
  return port ? `${host}:${port}` : host;
}
